import { fetchAuthSession } from "aws-amplify/auth";
import { COMPETITION_API_BASE } from "./competitionApiEnvironment";

const BASE_PATH = "/competition/me/result-claims";

async function request(path, { expectedUserId, ...options } = {}) {
  const session = await fetchAuthSession();
  const tokens = session?.tokens;
  const token = tokens?.idToken?.toString() || tokens?.accessToken?.toString();
  const subject = tokens?.idToken?.payload?.sub || tokens?.accessToken?.payload?.sub || session?.userSub;
  if (!token || (expectedUserId && subject !== expectedUserId)) {
    const error = new Error("Your sign-in changed. Sign in again to manage this claim.");
    error.status = 401;
    throw error;
  }
  const response = await fetch(`${COMPETITION_API_BASE}${path}`, {
    ...options,
    cache: "no-store",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.error?.message || "Your result claim could not be updated. Please try again.");
    error.status = response.status;
    error.data = payload.data;
    throw error;
  }
  return payload;
}

function claimPath({ family, matchId, participantId }) {
  if (!["arc", "bullseye"].includes(family) || !matchId || !participantId) {
    throw new Error("This result does not have a claimable participant identity.");
  }
  return `${BASE_PATH}/${[family, matchId, participantId].map(encodeURIComponent).join("/")}`;
}

function claimItem(payload) {
  if (!payload?.data || typeof payload.data.claimed !== "boolean") throw new Error("Your claim status could not load. Please try again.");
  return payload.data;
}

export async function listMyResultClaims({ limit = 25, nextToken, signal, expectedUserId } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (nextToken) params.set("nextToken", nextToken);
  const payload = await request(`${BASE_PATH}?${params}`, { signal, expectedUserId });
  if (!Array.isArray(payload.data)) throw new Error("Your claimed results could not load. Please try again.");
  return { data: payload.data, nextToken: payload.meta?.nextToken || payload.nextToken || null };
}

export async function getResultClaim(target) {
  const payload = await request(claimPath(target), { signal: target.signal, expectedUserId: target.expectedUserId });
  return claimItem(payload);
}

export async function claimResult(target) {
  const payload = await request(claimPath(target), { method: "PUT", expectedUserId: target.expectedUserId });
  return claimItem(payload);
}

export async function removeResultClaim(target) {
  const payload = await request(claimPath(target), { method: "DELETE", expectedUserId: target.expectedUserId });
  return claimItem(payload);
}
